import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  input,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  NgpSelect,
  NgpSelectDropdown,
  NgpSelectOption,
  NgpSelectPortal,
} from 'ng-primitives/select';
import { LabeledOption, normalizeLabeledOptions } from '../shared/labeled-option';

export type SelectOption = LabeledOption;

/**
 * A single-choice dropdown.
 *
 * Built on ng-primitives' full custom NgpSelect (trigger + portal +
 * listbox) rather than NgpNativeSelect — this version owns its whole
 * visual presentation, including the open dropdown panel, instead of
 * handing off to the OS picker. NgpSelect puts role="combobox",
 * aria-expanded/-controls/-activedescendant, tabindex and keyboard
 * handling directly on the trigger; NgpSelectOption puts role="option"
 * and aria-selected on each row. None of that needs adding by hand.
 * State to style against: [data-open] [data-disabled] [data-hover]
 * [data-press] [data-focus] [data-focus-visible] on the trigger;
 * [data-selected] [data-active] [data-hover] [data-press]
 * [data-disabled] on each option.
 *
 * `options` accepts plain strings or {value, label, disabled?} objects,
 * same shape as the NgpNativeSelect version this replaces.
 *
 * `name`/`required` are accepted for API continuity with the previous
 * native-<select>-backed version, but the trigger is a <div>, not a
 * form-associated element — there is nothing for `name` to attach to
 * natively, so it is applied as a plain (non-functional) attribute.
 * `required` does drive aria-required, which is the correct signal for
 * a non-native combobox.
 *
 * `label` and `error` behave exactly as they do on hh-input, including
 * the same gap: `error`'s hint text and red-ring styling are always
 * correct, but it does not drive aria-invalid/aria-describedby — see
 * hh-input.ts for the full reasoning (NgpSelect uses the same
 * ngpFormControl composable NgpInput does, with the same NgpFormField
 * discovery mismatch).
 */
@Component({
  selector: 'hh-select',
  standalone: true,
  imports: [NgpSelect, NgpSelectPortal, NgpSelectDropdown, NgpSelectOption],
  template: `
    <div class="hh-field">
      @if (label()) {
        <label class="hh-field__label" [for]="selectId">{{ label() }}</label>
      }

      <div
        ngpSelect
        class="hh-select__trigger"
        [class.hh-select__trigger--error]="!!error()"
        [id]="selectId"
        [attr.name]="name() || null"
        [attr.aria-required]="required() ? 'true' : null"
        [ngpSelectValue]="ngpValue()"
        (ngpSelectValueChange)="handleValueChange($event)"
        [ngpSelectDisabled]="disabled()"
        (blur)="onTouched()"
      >
        @if (selectedLabel(); as selected) {
          <span class="hh-select__value">{{ selected }}</span>
        } @else {
          <span class="hh-select__placeholder">{{ placeholder() || 'Select an option' }}</span>
        }
        <span class="hh-select__caret" aria-hidden="true">&#9660;</span>

        <div *ngpSelectPortal ngpSelectDropdown class="hh-select__dropdown">
          @for (option of normalizedOptions(); track option.value) {
            <div
              [ngpSelectOptionValue]="option.value"
              [ngpSelectOptionDisabled]="option.disabled"
              ngpSelectOption
              class="hh-select__option"
            >
              {{ option.label }}
            </div>
          } @empty {
            <div class="hh-select__empty">No options</div>
          }
        </div>
      </div>

      @if (error()) {
        <span class="hh-field__hint hh-field__hint--error">{{ error() }}</span>
      }
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HhSelectComponent),
      multi: true,
    },
  ],
  styleUrl: './hh-select.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HhSelectComponent implements ControlValueAccessor {
  private static nextId = 0;
  protected readonly selectId = `hh-select-${HhSelectComponent.nextId++}`;

  readonly options = input<(SelectOption | string)[]>([]);
  readonly placeholder = input('');
  readonly name = input('');
  readonly required = input(false, { transform: booleanAttribute });

  /** Optional label above the field. Unset renders no label at all. */
  readonly label = input('');

  /** Optional error hint below the field (see the class doc comment). */
  readonly error = input('');

  protected readonly normalizedOptions = computed<SelectOption[]>(() =>
    normalizeLabeledOptions(this.options()),
  );

  protected readonly selectedLabel = computed(() => {
    const val = this.value();
    if (!val) return undefined;
    return this.normalizedOptions().find((o) => o.value === val)?.label;
  });

  /** Written by forms via setDisabledState, not bound by callers. */
  protected readonly disabled = signal(false);
  protected readonly value = signal('');

  /** NgpSelect's own sentinel for "nothing selected" is undefined, not
   * our '' — bridge at the boundary so this component's own CVA keeps
   * speaking plain string, same as every other hh-* form control. */
  protected readonly ngpValue = computed(() => this.value() || undefined);

  private onChange: (val: string) => void = () => {};
  protected onTouched: () => void = () => {};

  protected handleValueChange(next: string | undefined): void {
    const val = next ?? '';
    this.value.set(val);
    this.onChange(val);
  }

  writeValue(val: string): void {
    this.value.set(val ?? '');
  }

  registerOnChange(fn: (val: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
